import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {
  CompanyBasicInfo,
  CompanyBenefits,
  CompanyHeader,
  CompanyJobReviews,
  DownloadAppJobdetail,
} from '@jobseeker/lotus/components';
import CompanyJobFeed from '@jobseeker/feature-job/components/companyJobfeed';
import JobsJsonLd from '@jobseeker/feature-job/components/JobItemListingSchema';
import {Box, Grid, Skeleton, styled, Tab, Tabs} from '@mui/material';
import {handleTracking} from '@jobseeker/common-core/utils';
import JobfeedHarness from '@jobseeker/client-harness/components/JobHarness/JobfeedV2';
import {memo, useEffect, useState} from 'react';
import {AppState, Job} from '@jobseeker/common-core/types';
import {findCompanySimilarJobs} from '@jobseeker/common-core/reducers/job/thunkServices/jobfeed';
// import {findCompanyJobs} from '@jobseeker/common-core/reducers/job/thunkServices/companyJobfeed'
import NotFoundPage from '../../../pages/404';
import {jobfeedService} from '@jobseeker/common-core/services';
import dynamic from 'next/dynamic';
import JobsByLocation from '@components/JobDetail/JobsByLocation';
import BlogsPanel from '@jobseeker/feature-ads/components/BlogsPanel';
import {COMPANY} from '@constants';
import {capitalizeFirstLetter} from '@utils';

const NoAppliedJobs = styled('div')(({theme}) => ({
  borderRadius: '12px',
  boxShadow: '0 7px 14px 0 rgba(0, 0, 0, 0.01)',
  backgroundColor: '#fff',
  marginTop: '16px',
  padding: '22px 22px 22px 22px',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    marginTop: 0,
    paddingTop: '16px',
  },
}));

type ABdataProps = {
  ambitionboxCompanyId: number;
  benefits: {
    totalBenefits: number;
    benefits: [
      {
        name: string;
        iconUrl: string;
        count: number;
        totalCount: number;
      },
    ];
    benefitsUrl: string;
    usersReportedBenefits: number;
  };
  companyInfo: {
    ambitionBoxCompanyId: number;
    companyName: string;
    reviewsCount: number;
    companyLogo: string;
    aboutCompany: string;
    foundedYear: string;
    totalEmployees: string;
    totalEmployeesIndia: string;
    companyType: string;
    primaryIndustry: string;
    natureOfBusiness: [string];
    hq: string;
    officeLocations: [
      {
        cityName: string;
      },
    ];
    otherTags: [string];
    title: string;
    url: string;
    aggregateRating: number;
    aggregateRatingOneDecimal: number;
  };
  topJobProfiles: {
    entities: [
      {
        name: string;
        url: string;
        count: number;
        rating: number;
      },
    ];
    topJobProfilesUrl: string;
  };
};

type companyJobProps = {
  jobs: Job[];
  hasMore: boolean;
};

const Company = ({
  companyId,
  ambitionBoxData,
  slug,
  companyJobs,
  companyRefCode,
}: {
  ambitionBoxData: ABdataProps;
  companyId: string | undefined;
  slug: string | undefined;
  companyJobs: {jobs: Job[]; hasMore: boolean};
  companyRefCode?: string;
}) => {
  const jobFeedStore = useSelector((state: AppState) => state.job);
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const [checkCom, setCheckCom] = useState<Map<string, string | undefined>>(
    () => new Map()
  );

  const [pageNo, setPageNo] = useState<number>(0);
  const [concat, setConcat] = useState<boolean>(false);

  const [CpageNo, setCPageNo] = useState<number>(0);
  const [Cconcat, setCConcat] = useState<boolean>(false);
  const [companyJobList, setCompanyJobList] = useState<companyJobProps>({
    jobs: companyJobs?.jobs || [],
    hasMore: companyJobs?.hasMore || false,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSimilarLoading, setIsSimilarLoading] = useState<boolean>(false);
  const [value, setValue] = useState(0);
  const {device} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );
  const isDesktop = device == 'desktop';

  const page = useSelector((state: AppState) => state.app.page, shallowEqual);
  const AdsPanelHarness = memo(
    dynamic(
      () =>
        import('@jobseeker/client-harness/components/AdsHarness/AdsPanelV2'),
      {
        ssr: false,
      }
    )
  );

  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();
  // const [appliedJobs, setAppliedJobs] = useState<boolean>(true);
  useEffect(() => {
    setIsSimilarLoading(true);
    try {
      dispatch(findCompanySimilarJobs({page: pageNo, concat: concat}));
    } catch (error) {
      console.log('🚀 ~ file: [jobslug].tsx:179 ~ store=> ~ error:', error);
    }
    setIsSimilarLoading(false);
  }, [pageNo]);

  useEffect(() => {
    setIsLoading(true);
    const fetchCompanyJobs = async () => {
      try {
        // Fetch company jobs asynchronously

        const com_jobs = isAppIimjobs
          ? await jobfeedService.getCompanyJobs(slug || '', CpageNo)
          : await jobfeedService.getCompanyJobs(slug || '', CpageNo, 1);

        // Append new jobs to the previous state

        if (Cconcat) {
          setCompanyJobList(prevState => ({
            jobs: [...prevState.jobs, ...com_jobs.data.jobs], // Append new jobs
            hasMore: com_jobs.data.hasMore, // Check if there are more jobs
          }));
        } else {
          setCompanyJobList({
            jobs: [...com_jobs.data.jobs], // Append new jobs
            hasMore: com_jobs.data.hasMore, // Check if there are more jobs
          });
        }
      } catch (error) {
        console.error('Error fetching company jobs:', error);
      }
    };

    fetchCompanyJobs();
    setIsLoading(false);
  }, [CpageNo]);

  if (
    !companyId ||
    companyId == '' ||
    companyId == undefined ||
    !ambitionBoxData ||
    Object.keys(ambitionBoxData).length === 0
  ) {
    return <NotFoundPage />;
  }

  // const [checkJobs, setCheckJobs] = useState<Set<number>>(new Set());

  const onLoadMore = () => {
    setPageNo(pageNo + 1);
    setConcat(true);
  };

  const onCompanyLoadMore = () => {
    setCPageNo(CpageNo + 1);
    setCConcat(true);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const sendEvent = (
    eventCatogary: string,
    eventName: string,
    action: string
  ) => {
    handleTracking(`${eventName}_${action}`, {
      event_category: `${eventCatogary}`,
      Origin: page,
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
      Php_SessionID_Mod: sessionStorage.getItem('phpSessId') || '0',
    });
  };
  return (
    <>
      {!isAppIimjobs && ambitionBoxData?.companyInfo?.companyName ? (
        <CompanyHeader
          isdesktop={isDesktop}
          companyInfo={ambitionBoxData?.companyInfo}
          companyId={companyId}
          companyListingLogo={
            companyJobList.jobs?.[0]?.companyData?.companyLogoFilePath ??
            ambitionBoxData?.companyInfo?.companyLogo
          }
        />
      ) : (
        <></>
      )}

      <Grid
        container
        sx={{mt: isAppIimjobs ? 0 : 4}}
        columnSpacing={{xxs: 0, md: isAppIimjobs ? 2 : 4}}
        direction={'row-reverse'}
        mx={
          isDesktop || !isAppIimjobs ? {xxs: 1, md: isAppIimjobs ? -2 : 0} : -1
        }
        width={isDesktop || !isAppIimjobs ? 'auto' : 'calc(100% + 16px)'}
        padding={
          isAppIimjobs
            ? 0
            : {xxs: 0, md: '0px 60px 0px 0px', lg: '0px 84px 0px 16px'}
        }
      >
        <Grid item xxs={12} md={3.5} lg={3.13}>
          {' '}
          {isDesktop ? (
            <CompanyJobReviews
              companyName={ambitionBoxData?.companyInfo?.companyName}
              companyId={Number(companyId)}
              jobReviewData={ambitionBoxData?.topJobProfiles}
            />
          ) : (
            <></>
          )}
          {isAppIimjobs || device != 'desktop' ? (
            <></>
          ) : (
            <>
              <AdsPanelHarness
                variant={VersionVariants.VERSION_TWO}
                showSmallBadges={false}
                showFeatured={false}
                showLearning={true}
                showLearningCenterCTA={false}
                showLearnGrow={true}
                page={COMPANY}
              />
              <JobsByLocation />
              <DownloadAppJobdetail sendEvent={sendEvent} />
              <Box mt={3.75}>
                <BlogsPanel heading="Interview Questions for you" />
              </Box>
            </>
          )}
        </Grid>
        <Grid item xxs={12} md={8.5} lg={8.86}>
          {isAppIimjobs && ambitionBoxData?.companyInfo?.companyName ? (
            <CompanyHeader
              isdesktop={isDesktop}
              companyInfo={ambitionBoxData?.companyInfo}
              companyId={companyId}
              companyListingLogo={
                companyJobList.jobs?.[0]?.companyData?.companyLogoFilePath ??
                ambitionBoxData?.companyInfo?.companyLogo
              }
              data-testid="company-header"
            />
          ) : (
            <></>
          )}
          {!isDesktop && isAppIimjobs ? (
            <Box
              sx={{
                width: '100%',
                backgroundColor: 'white',
                mt: 2,
                borderRadius: 3,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                paddingLeft: 1.5,
              }}
            >
              <Tabs
                value={value}
                onChange={handleChange}
                variant="fullWidth"
                TabIndicatorProps={{
                  style: {
                    backgroundColor: 'primary', // Customize underline color
                    height: '4px', // Adjust thickness
                    borderTopRightRadius: 4,
                    borderTopLeftRadius: 4,
                  },
                }}
                textColor="inherit"
                sx={{mr: 18}}
              >
                <Tab
                  label="Overview"
                  sx={{
                    color: value === 0 ? '#333' : '#7E7E7E', // Black for selected, gray for others
                    fontWeight: 500, // Optional: Bold for the selected
                    fontSize: 14,
                    paddingTop: 3,
                    paddingBottom: 0.5,
                  }}
                />
                <Tab
                  label="Jobs"
                  sx={{
                    color: value === 1 ? '#333' : '#7E7E7E', // Black for selected, gray for others
                    fontWeight: 500, // Optional: Bold for the selected
                    fontSize: 14,
                    paddingTop: 3,
                    paddingBottom: 0.5,
                  }}
                />
              </Tabs>
            </Box>
          ) : (
            <></>
          )}
          {isDesktop || (!isDesktop && value == 0) ? (
            <>
              <CompanyBasicInfo companyInfo={ambitionBoxData?.companyInfo} />
              <CompanyBenefits
                benefitsInfo={ambitionBoxData?.benefits}
                companyName={ambitionBoxData?.companyInfo?.companyName}
              />
            </>
          ) : (
            <></>
          )}
          {!isDesktop && value == 0 ? (
            <CompanyJobReviews
              companyName={ambitionBoxData?.companyInfo?.companyName}
              companyId={Number(companyId)}
              jobReviewData={ambitionBoxData?.topJobProfiles}
            />
          ) : (
            <></>
          )}
          {isDesktop || !isAppIimjobs || (!isDesktop && value == 1) ? (
            isLoading && CpageNo == 0 ? (
              <Box
                sx={{
                  pt: 2.4,
                }}
              >
                {[...new Array(15)].map(index => {
                  return (
                    <Box display="flex" key={`jobfeed-skel-${index}`}>
                      <Skeleton
                        variant="rounded"
                        height={60}
                        sx={{
                          width: {xxs: '75px', md: '65px'},
                          mt: {xxs: 0, md: 1},
                          ml: 2,
                          mb: 1,
                        }}
                      />
                      <Box display="block" width="100%">
                        <Skeleton
                          variant="rounded"
                          width={'50%'}
                          height={20}
                          sx={{
                            mt: {xxs: 0, md: 1},
                            ml: {xxs: 2, md: 'none'},
                            mb: 1,
                          }}
                        />
                        <Skeleton
                          variant="rounded"
                          width={'90%'}
                          height={20}
                          sx={{
                            mt: {xxs: 0, md: 1},
                            ml: {xxs: 2, md: 'none'},
                            mb: 1,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : !companyJobList.jobs.length ? (
              <NoAppliedJobs>
                <h1 style={{fontSize: 16, fontWeight: 'bold'}}>
                  {ambitionBoxData?.companyInfo?.companyName ||
                    capitalizeFirstLetter(slug || '')}{' '}
                  Jobs
                </h1>
                <p
                  style={{marginTop: 1, marginBottom: 0, fontSize: 14}}
                  data-nosnippet="true"
                >
                  There are no jobs available from{' '}
                  <b>
                    {ambitionBoxData?.companyInfo?.companyName ||
                      capitalizeFirstLetter(slug || '')}
                  </b>
                  . However, you may browse jobs from other companies.
                </p>
              </NoAppliedJobs>
            ) : (
              <Box mb={{xxs: 2, md: 0}} mt={{xxs: 0, md: 2}}>
                {
                  <JobsJsonLd
                    name={`${
                      ambitionBoxData?.companyInfo?.companyName ||
                      capitalizeFirstLetter(slug || '')
                    } Jobs`}
                    jobs={companyJobs?.jobs}
                  />
                }
                {companyJobList.jobs.length ? (
                  <CompanyJobFeed
                    title={`${
                      ambitionBoxData?.companyInfo?.companyName ||
                      capitalizeFirstLetter(slug || '')
                    } Jobs`}
                    onLoadMore={onCompanyLoadMore}
                    companyJobs={companyJobList}
                    setCompanyJobList={setCompanyJobList}
                    checkJobs={checkCom}
                    setCheckJobs={setCheckCom}
                    companyRefCode={companyRefCode}
                  />
                ) : (
                  <></>
                )}
              </Box>
            )
          ) : (
            <></>
          )}
          {isDesktop || !isAppIimjobs || (!isDesktop && value == 1) ? (
            isSimilarLoading && pageNo == 0 ? (
              <Box
                sx={{
                  pt: 2.4,
                }}
              >
                {[...new Array(15)].map(index => {
                  return (
                    <Box display="flex" key={`jobfeed-skel-${index}`}>
                      <Skeleton
                        variant="rounded"
                        height={60}
                        sx={{
                          width: {xxs: '75px', md: '65px'},
                          mt: {xxs: 0, md: 1},
                          ml: 2,
                          mb: 1,
                        }}
                      />
                      <Box display="block" width="100%">
                        <Skeleton
                          variant="rounded"
                          width={'50%'}
                          height={20}
                          sx={{
                            mt: {xxs: 0, md: 1},
                            ml: {xxs: 2, md: 'none'},
                            mb: 1,
                          }}
                        />
                        <Skeleton
                          variant="rounded"
                          width={'90%'}
                          height={20}
                          sx={{
                            mt: {xxs: 0, md: 1},
                            ml: {xxs: 2, md: 'none'},
                            mb: 1,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box
                mb={{xxs: 2, md: 0}}
                mt={2}
                ml={isAppIimjobs ? 0 : {xxs: 0, md: -2}}
              >
                {jobFeedStore.jobfeed.length &&
                !companyJobList.hasMore &&
                companyJobList?.jobs?.length < 10 ? (
                  <JobfeedHarness
                    onLoadMore={onLoadMore}
                    routeToJobDetail={() => {}}
                    title={
                      isAppIimjobs
                        ? ' Similar jobs that you might be interested in'
                        : 'Below are some more jobs from similar companies'
                    }
                    feedOnly={true}
                    infiniteScroll={true}
                    variant={VersionVariants.VERSION_TWO}
                    checkJobs={checkCom}
                    // checkJobs={checkJobs}
                    // setCheckJobs={memoizedSetCheckJobs}
                  />
                ) : (
                  <></>
                )}
              </Box>
            )
          ) : (
            <></>
          )}
        </Grid>
      </Grid>
    </>
  );
};
export default Company;
